import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroImage } from "../data/mock";
import { ArrowRight, ChevronRight, Star, Aperture, Zap, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useInView(ref, threshold = 0.15) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold]);
  return inView;
}

function HeroSection() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = heroImage;
    img.onload = () => setLoaded(true);
    const timer = setTimeout(() => setLoaded(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image */}
      <div
        className={`absolute inset-0 transition-opacity duration-1000 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
      >
        <img
          src={heroImage}
          alt="Basketball action shot"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20">
        <div className="max-w-2xl">
          <div
            className={`transition-all duration-700 delay-300 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="inline-block text-amber-500 font-semibold text-sm tracking-[0.2em] uppercase mb-6">
              Sports Photography
            </span>
          </div>
          <h1
            className={`font-bebas text-6xl sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.9] tracking-tight text-white mb-6 transition-all duration-700 delay-500 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            See The Game
            <br />
            <span className="text-amber-500">Differently!</span>
          </h1>
          <p
            className={`text-neutral-400 text-lg sm:text-xl leading-relaxed max-w-lg mb-10 transition-all duration-700 delay-700 ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Professional sports & event photography that captures the raw intensity, emotion, and beauty of competition.
          </p>
          <div
            className={`flex flex-wrap gap-4 transition-all duration-700 delay-[900ms] ${
              loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <Link to="/portfolio">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold text-base px-8 py-6 rounded-md group">
                View Portfolio
                <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-neutral-600 text-white hover:bg-white/10 hover:border-neutral-400 font-semibold text-base px-8 py-6 rounded-md"
              >
                Book a Shoot
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
        <span className="text-neutral-600 text-xs uppercase tracking-widest">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-amber-500/60 to-transparent animate-pulse" />
      </div>
    </section>
  );
}

function FeaturedWork() {
  const ref = useRef(null);
  const inView = useInView(ref);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    axios.get(`${API}/portfolio/featured`)
      .then((res) => setFeatured(res.data))
      .catch((err) => console.error("Failed to load featured items:", err));
  }, []);

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`flex flex-col sm:flex-row sm:items-end justify-between mb-14 gap-4 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div>
            <span className="text-amber-500 text-sm font-semibold tracking-[0.2em] uppercase">
              Selected Work
            </span>
            <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-2">
              Featured Shots
            </h2>
          </div>
          <Link
            to="/portfolio"
            className="flex items-center gap-2 text-neutral-400 hover:text-amber-500 text-sm font-medium transition-colors duration-200 group"
          >
            View All Work
            <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((item, index) => (
            <div
              key={item.id}
              className={`group relative overflow-hidden rounded-lg cursor-pointer aspect-[4/3] transition-all duration-700 ${
                inView
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-12"
              }`}
              style={{ transitionDelay: inView ? `${index * 100 + 200}ms` : "0ms" }}
            >
              <img
                src={item.image}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                <span className="text-amber-500 text-xs font-semibold uppercase tracking-wider">
                  {item.category}
                </span>
                <h3 className="font-bebas text-2xl text-white mt-1 tracking-wide">
                  {item.title}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhySection() {
  const ref = useRef(null);
  const inView = useInView(ref);

  const features = [
    {
      icon: Aperture,
      title: "Pro-Grade Gear",
      desc: "Top-tier camera bodies and fast lenses for tack-sharp images in any lighting condition.",
    },
    {
      icon: Zap,
      title: "Fast Delivery",
      desc: "Edited highlights within 24 hours. Full galleries delivered in 48 hours or less.",
    },
    {
      icon: Users,
      title: "Game Knowledge",
      desc: "Years of courtside experience means I know where the action will be before it happens.",
    },
  ];

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0e0e0e]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-amber-500 text-sm font-semibold tracking-[0.2em] uppercase">
            Why Choose Us
          </span>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-2">
            Built For The Big Moments
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <div
              key={i}
              className={`bg-[#141414] border border-[#1e1e1e] rounded-xl p-8 hover:border-amber-500/30 transition-all duration-500 group ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
              style={{ transitionDelay: inView ? `${i * 150 + 200}ms` : "0ms" }}
            >
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center mb-6 group-hover:bg-amber-500/20 transition-colors duration-300">
                <f.icon className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="font-bebas text-2xl tracking-wide text-white mb-3">{f.title}</h3>
              <p className="text-neutral-500 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialSection() {
  const ref = useRef(null);
  const inView = useInView(ref);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`)
      .then((res) => setTestimonials(res.data))
      .catch((err) => console.error("Failed to load testimonials:", err));
  }, []);

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-amber-500 text-sm font-semibold tracking-[0.2em] uppercase">
            Testimonials
          </span>
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-2">
            What Clients Say
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((t, i) => (
            <div
              key={t.id}
              className={`bg-[#141414] border border-[#1e1e1e] rounded-xl p-8 transition-all duration-700 ${
                inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
              style={{ transitionDelay: inView ? `${i * 150 + 200}ms` : "0ms" }}
            >
              <div className="flex gap-1 mb-5">
                {Array.from({ length: t.rating }).map((_, idx) => (
                  <Star key={idx} className="w-4 h-4 fill-amber-500 text-amber-500" />
                ))}
              </div>
              <p className="text-neutral-300 text-sm leading-relaxed mb-6 italic">
                "{t.text}"
              </p>
              <div>
                <p className="text-white font-semibold text-sm">{t.name}</p>
                <p className="text-neutral-500 text-xs mt-1">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const ref = useRef(null);
  const inView = useInView(ref);

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0e0e0e]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          className={`text-center max-w-2xl mx-auto transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <h2 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mb-6">
            Ready To Capture Your
            <br />
            <span className="text-amber-500">Next Big Moment?</span>
          </h2>
          <p className="text-neutral-400 text-lg mb-10 leading-relaxed">
            Whether it's a championship game, a team photoshoot, or a live concert — let's create something unforgettable.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold text-base px-8 py-6 rounded-md group">
                Get In Touch
                <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button
                variant="outline"
                className="border-neutral-600 text-white hover:bg-white/10 hover:border-neutral-400 font-semibold text-base px-8 py-6 rounded-md"
              >
                View Portfolio
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <>
      <HeroSection />
      <FeaturedWork />
      <WhySection />
      <CTASection />
    </>
  );
}
